import { useMemo, useState, useRef } from "react";
import { BaseDateDataChart } from "../../../../components/charts/BaseDateDataChart/BaseDateDataChart";
import { LineChartColors } from "../../../../components/charts/constants";
import { useProductTrendingData } from "./hooks/useTrendingProductData";
import { convertProductTimeSeriesData } from "./utils";
import useMatchProductsById from "./hooks/useMatchProductsById";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { PeriodSelector } from "../../../../components/PeriodSelector";
import { determineChartContent, hasNoData } from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { CHART_CONSTANTS } from "../constants";

const { baseId, tooltip, chartTitle } =
  CHART_CONSTANTS.keyPlayerNewProductDetections;
interface Props {
  isLoading: boolean;
  topProductIds: number[];
}

export function KeyPlayerNewProductDetectionsChart(props: Props) {
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const chartRef = useRef<am4charts.XYChart>(null);
  const combinedFilters = useCombinedFilters();
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { marketTimeSeriesProductBreakdown, loading: isLoadingTrendingData } =
    useProductTrendingData({
      companyFilters: combinedFilters || {},
      ids: props.topProductIds,
      lookBackYears: Number.parseInt(selectedPeriod),
      localizeInstalls,
    });

  const { productsNameMapById } = useMatchProductsById(
    props.topProductIds ?? []
  );

  const chartData = useMemo(
    () =>
      convertProductTimeSeriesData(
        marketTimeSeriesProductBreakdown,
        productsNameMapById
      ),
    [marketTimeSeriesProductBreakdown, productsNameMapById]
  );

  const isLoading = isLoadingTrendingData || props.isLoading;

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [chartData, isLoading]
  );
  const csvFieldMappings = useMemo(() => {
    const mappings: Record<string, string> = {
      date: "Date",
    };

    if (
      !chartData ||
      isLoading ||
      chartData.length === 0 ||
      !chartData[0].series
    ) {
      return mappings;
    }

    const [firstDataPoint] = chartData;
    firstDataPoint.series.forEach((seriesItem) => {
      const baseKey = seriesItem.name.toLowerCase().replace(/[\s&]+/g, "_");
      mappings[`series_${baseKey}`] = `${seriesItem.name} (%)`;
      mappings[`series_${baseKey}_count`] =
        `${seriesItem.name} (# of companies)`;
    });

    return mappings;
  }, [chartData, isLoading]);

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BaseDateDataChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={LineChartColors}
          yAxisLabel="New Product Detections"
          ref={chartRef}
        />
      ),
    });
  }, [chartData, noDataReturned, isLoading]);

  function handlePeriodSelect(period: string) {
    setSelectedPeriod(period);
  }

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned}
      chartRef={chartRef}
      isLoading={isLoading}
      titleExtras={
        <PeriodSelector
          id={`${baseId}-period-selector`}
          selectedPeriod={selectedPeriod}
          onChange={handlePeriodSelect}
          maxYears={5}
        />
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
}
